import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  EmailDrafterState,
  EmailDraftSession,
  HistoryItem,
  EmailDraftRequest,
} from '@/types/email-drafter'
import { emailDrafterService } from '@/services/email-drafter'

interface EmailDrafterStore extends EmailDrafterState {
  // Actions
  updateSession: (session: Partial<EmailDraftSession>) => void
  sendEmailDraft: () => Promise<void>
  loadHistory: () => void
  deleteHistoryItem: (id: string) => void
  clearHistory: () => void
  clearForm: () => void
  loadHistoryItem: (item: HistoryItem) => void
  setResponse: (response: string | null) => void
  setError: (error: string | null) => void
  exportData: () => void
  importData: (file: File) => Promise<void>
}

const initialSession: EmailDraftSession = {
  emailAddress: '',
  emailName: '',
  emailSubject: '',
  emailContent: '',
  systemprompts: {
    categorization: '',
    emaildrafter: '',
    missionsolar: '',
    neoom: '',
    selfsolve: '',
  },
}

export const useEmailDrafterStore = create<EmailDrafterStore>()(
  persist(
    (set, get) => ({
      session: initialSession,
      history: [],
      stats: {
        totalTests: 0,
        successTests: 0,
        totalResponseTime: 0,
      },
      isLoading: false,
      error: null,
      response: null,

      updateSession: (session) => {
        set((state) => ({
          session: { ...state.session, ...session },
        }))
        // Auto-save to localStorage
        const currentSession = get().session
        emailDrafterService.saveToLocalStorage('session', currentSession)
      },

      sendEmailDraft: async () => {
        const { session } = get()

        // Validate required fields
        if (!session.emailAddress || !session.emailName || !session.emailContent) {
          set({ error: 'Bitte füllen Sie alle Pflichtfelder aus.' })
          return
        }

        set({ isLoading: true, error: null, response: null })

        const request: EmailDraftRequest = {
          email_address: session.emailAddress,
          email_name: session.emailName,
          email_subject: session.emailSubject,
          email_content: session.emailContent,
          systemprompt_categorization: session.systemprompts.categorization || undefined,
          systemprompt_emaildrafter: session.systemprompts.emaildrafter || undefined,
          systemprompt_missionsolar: session.systemprompts.missionsolar || undefined,
          systemprompt_neoom: session.systemprompts.neoom || undefined,
          systemprompt_selfsolve: session.systemprompts.selfsolve || undefined,
          timestamp: new Date().toISOString(),
        }

        try {
          const response = await emailDrafterService.sendEmailDraft(request)

          set({
            response,
            isLoading: false,
          })

          // Update stats
          const stats = emailDrafterService.getStats()
          set({ stats })

          // Reload history
          get().loadHistory()
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten',
            isLoading: false,
          })
        }
      },

      loadHistory: () => {
        const history = emailDrafterService.getHistory()
        const stats = emailDrafterService.getStats()
        set({ history, stats })
      },

      deleteHistoryItem: (id) => {
        emailDrafterService.deleteHistoryItem(id)
        get().loadHistory()
      },

      clearHistory: () => {
        emailDrafterService.clearHistory()
        set({ history: [] })
      },

      clearForm: () => {
        set({
          session: initialSession,
          response: null,
          error: null,
        })
        emailDrafterService.saveToLocalStorage('session', initialSession)
      },

      loadHistoryItem: (item) => {
        const session: EmailDraftSession = {
          emailAddress: item.request.email_address,
          emailName: item.request.email_name,
          emailSubject: item.request.email_subject,
          emailContent: item.request.email_content,
          systemprompts: {
            categorization: item.request.systemprompt_categorization || '',
            emaildrafter: item.request.systemprompt_emaildrafter || '',
            missionsolar: item.request.systemprompt_missionsolar || '',
            neoom: item.request.systemprompt_neoom || '',
            selfsolve: item.request.systemprompt_selfsolve || '',
          },
        }

        set({
          session,
          response: item.response,
          error: null,
        })

        emailDrafterService.saveToLocalStorage('session', session)
      },

      setResponse: (response) => set({ response }),
      setError: (error) => set({ error }),

      exportData: () => {
        emailDrafterService.exportData()
      },

      importData: async (file) => {
        try {
          await emailDrafterService.importData(file)
          get().loadHistory()

          // Load imported session
          const session = emailDrafterService.loadFromLocalStorage<EmailDraftSession>('session')
          if (session) {
            set({ session })
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Import fehlgeschlagen',
          })
        }
      },
    }),
    {
      name: 'email-drafter-store',
      partialize: (state) => ({
        session: state.session,
      }),
    }
  )
)